import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Separator } from "../../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Users,
  UserPlus,
  MoreVertical,
  Trash2,
  Edit,
  Loader2,
  Copy,
  Check,
  AlertTriangle,
  KeyRound,
  Ban,
  UserCheck,
  Search,
  Download,
  Upload,
  X,
  Mail,
  Shield,
  Calendar,
  FolderOpen,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ORG_ROLE } from "../../lib/auth";
import { apiErrorMessage } from "../../lib/apiError";

const AdminUsersPage = () => {
  const { user, isSuperAdmin, isAdmin } = useAuth();
  // Mirror backend valid_org_roles (Roles_and_Permissions_Spec §5): both admin
  // AND super_admin may grant the Admin role. (super_admin is never API/UI
  // grantable — seeded via CLI only — so it never appears as an option.)
  const canGrantAdmin = () => isAdmin() || isSuperAdmin();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialogs
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);

  // User detail panel
  const [detailUser, setDetailUser] = useState(null);
  const [detailProjects, setDetailProjects] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Form states
  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    role: ORG_ROLE.MEMBER,
    is_planner: false,
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [newIsPlanner, setNewIsPlanner] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Bulk import
  const [bulkCsvText, setBulkCsvText] = useState("");
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [editUserForm, setEditUserForm] = useState({ name: "", email: "" });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const usersRes = await api.get("/admin/users");
      setUsers(usersRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter users
  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      !searchQuery ||
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    const matchesStatus =
      statusFilter === "all" || u.account_status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/admin/users/invite", inviteForm);
      setTempPassword(res.data.temp_password);
      toast.success("User invited successfully");
      await fetchData();
      setInviteForm({
        email: "",
        name: "",
        role: ORG_ROLE.MEMBER,
        is_planner: false,
      });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to invite user"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;
    setSubmitting(true);
    try {
      await api.patch(`/admin/users/${selectedUser.id}/role`, {
        role: newRole,
        is_planner: newIsPlanner,
      });
      toast.success("User role updated");
      setShowRoleDialog(false);
      setSelectedUser(null);
      await fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update role"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      await api.delete(`/admin/users/${selectedUser.id}`);
      toast.success("User removed");
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      if (detailUser?.id === selectedUser.id) setDetailUser(null);
      await fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to remove user"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      const res = await api.post(
        `/admin/users/${selectedUser.id}/reset-password`,
      );
      setTempPassword(res.data.temp_password);
      toast.success("Password reset successfully");
      await fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to reset password"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      await api.patch(`/admin/users/${selectedUser.id}/status`, {
        status: newStatus,
      });
      toast.success(
        `User ${newStatus === "suspended" ? "suspended" : "reactivated"} successfully`,
      );
      setShowStatusDialog(false);
      setSelectedUser(null);
      await fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update status"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await api.get("/admin/users/export", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "users_export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Users exported to CSV");
    } catch (error) {
      toast.error("Failed to export users");
    }
  };

  const handleBulkImport = async () => {
    if (!bulkCsvText.trim()) return;
    setSubmitting(true);
    setBulkImportResult(null);
    try {
      const lines = bulkCsvText
        .trim()
        .split("\n")
        .filter((l) => l.trim());
      const usersToImport = [];

      for (const line of lines) {
        const parts = line.split(",").map((s) => s.trim());
        if (parts.length >= 2) {
          usersToImport.push({
            name: parts[0],
            email: parts[1],
            role: parts[2] || ORG_ROLE.MEMBER,
          });
        }
      }

      if (usersToImport.length === 0) {
        toast.error("No valid entries found. Use format: Name, Email, Role");
        setSubmitting(false);
        return;
      }

      const res = await api.post("/admin/users/bulk-import", {
        users: usersToImport,
      });
      setBulkImportResult(res.data);
      toast.success(`Imported ${res.data.created} of ${res.data.total} users`);
      await fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Bulk import failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      // Skip header row if present
      const lines = text.split("\n");
      const firstLine = lines[0]?.toLowerCase() || "";
      const hasHeader =
        firstLine.includes("name") && firstLine.includes("email");
      setBulkCsvText(hasHeader ? lines.slice(1).join("\n") : text);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const openDetailPanel = async (u) => {
    setDetailUser(u);
    setLoadingDetail(true);
    try {
      const res = await api.get(`/admin/users/${u.id}/projects`);
      setDetailProjects(res.data);
    } catch {
      setDetailProjects([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    const updates = {};
    if (editUserForm.name && editUserForm.name !== selectedUser.name)
      updates.name = editUserForm.name;
    if (editUserForm.email && editUserForm.email !== selectedUser.email)
      updates.email = editUserForm.email;
    if (Object.keys(updates).length === 0) {
      setShowEditUserDialog(false);
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/admin/users/${selectedUser.id}`, updates);
      toast.success("User profile updated");
      setShowEditUserDialog(false);
      setSelectedUser(null);
      await fetchData();
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update user"));
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case ORG_ROLE.SUPER_ADMIN:
        return "bg-red-100 text-red-700 border-red-200";
      case ORG_ROLE.ADMIN:
        return "bg-purple-100 text-purple-700 border-purple-200";
      case ORG_ROLE.MEMBER:
        return "bg-blue-100 text-blue-700 border-blue-200";
      default:
        return "bg-slate-100 text-slate-600";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-200"
          >
            Active
          </Badge>
        );
      case "invited":
        return (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-200"
          >
            Invited
          </Badge>
        );
      case "suspended":
        return (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-700 border-red-200"
          >
            Suspended
          </Badge>
        );
      default:
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-200"
          >
            Active
          </Badge>
        );
    }
  };

  const formatRole = (role) => {
    return (
      role
        ?.split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ") || ""
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900">Users</h1>
          <Badge variant="outline" className="text-slate-500">
            {users.length} total
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowBulkImportDialog(true);
              setBulkCsvText("");
              setBulkImportResult(null);
            }}
          >
            <Upload className="h-4 w-4 mr-2" />
            Bulk Import
          </Button>
          <Button
            onClick={() => {
              setShowInviteDialog(true);
              setTempPassword("");
            }}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Create Account
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-[160px] h-9">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value={ORG_ROLE.ADMIN}>Admin</SelectItem>
            <SelectItem value={ORG_ROLE.MEMBER}>Member</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[160px] h-9">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="invited">Invited</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-6 sm:flex-row">
        {/* Users Table */}
        <Card className={detailUser ? "min-w-0 flex-1" : "w-full"}>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  {/* <TableHead className="text-center">Projects</TableHead> */}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="h-24 text-center text-slate-500"
                    >
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u) => (
                    <TableRow
                      key={u.id}
                      className={`cursor-pointer ${detailUser?.id === u.id ? "bg-slate-50" : ""}`}
                      onClick={() => openDetailPanel(u)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={getRoleBadgeColor(u.role)}
                        >
                          {formatRole(u.role)}
                        </Badge>
                        {u.is_planner && (
                          <Badge
                            variant="outline"
                            className="ml-1.5 bg-amber-50 text-amber-700 border-amber-200"
                          >
                            Planner
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(u.account_status)}</TableCell>
                      {/* <TableCell className="text-center">{u.project_count}</TableCell> */}
                      <TableCell
                        className="text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedUser(u);
                                setEditUserForm({
                                  name: u.name,
                                  email: u.email,
                                });
                                setShowEditUserDialog(true);
                              }}
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedUser(u);
                                setNewRole(u.role);
                                setNewIsPlanner(!!u.is_planner);
                                setShowRoleDialog(true);
                              }}
                              /* Self IS allowed here: an admin may self-grant the
                                 Planner role (spec Example B). The dialog disables
                                 the org-tier select for self (backend blocks a
                                 self-tier change); only the Planner toggle applies. */
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Change Role
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedUser(u);
                                setTempPassword("");
                                setShowResetPasswordDialog(true);
                              }}
                              disabled={u.id === user?.id}
                            >
                              <KeyRound className="h-4 w-4 mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {u.account_status !== "suspended" ? (
                              <DropdownMenuItem
                                className="text-amber-600"
                                onClick={() => {
                                  setSelectedUser(u);
                                  setShowStatusDialog(true);
                                }}
                                disabled={u.id === user?.id}
                              >
                                <Ban className="h-4 w-4 mr-2" />
                                Suspend Account
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                className="text-green-600"
                                onClick={() => {
                                  setSelectedUser(u);
                                  handleUpdateStatus("active");
                                }}
                              >
                                <UserCheck className="h-4 w-4 mr-2" />
                                Reactivate Account
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSelectedUser(u);
                                setShowDeleteConfirm(true);
                              }}
                              disabled={u.id === user?.id}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* User Detail Panel (flyout) */}
        {detailUser && (
          <Card className="w-full sm:w-80 sm:flex-shrink-0">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">User Details</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setDetailUser(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* User info */}
              <div className="text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-2">
                  <Users className="h-6 w-6 text-slate-500" />
                </div>
                <p className="font-semibold text-slate-900">
                  {detailUser.name}
                </p>
                <p className="text-xs text-slate-500">{detailUser.email}</p>
              </div>

              <Separator />

              {/* Details grid */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-400" />
                  <span className="text-xs text-slate-500 w-16">Role</span>
                  <Badge
                    variant="outline"
                    className={`text-xs ${getRoleBadgeColor(detailUser.role)}`}
                  >
                    {formatRole(detailUser.role)}
                  </Badge>
                  {detailUser.is_planner && (
                    <Badge
                      variant="outline"
                      className="text-xs bg-amber-50 text-amber-700 border-amber-200"
                    >
                      Planner
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-slate-400" />
                  <span className="text-xs text-slate-500 w-16">Status</span>
                  {getStatusBadge(detailUser.account_status)}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span className="text-xs text-slate-500 w-16">Created</span>
                  <span className="text-xs text-slate-700">
                    {new Date(detailUser.created_at).toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric", year: "numeric" },
                    )}
                  </span>
                </div>
                {detailUser.is_temp_password && (
                  <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-amber-500" />
                    <span className="text-xs text-amber-600 font-medium">
                      Temp password pending change
                    </span>
                  </div>
                )}
              </div>

              <Separator />

              {/* Projects */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FolderOpen className="h-4 w-4 text-slate-400" />
                  <span className="text-xs font-medium text-slate-700">
                    Projectss({detailUser.project_count})
                  </span>
                </div>
                {loadingDetail ? (
                  <div className="flex justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  </div>
                ) : detailProjects.length === 0 ? (
                  <p className="text-xs text-slate-400 pl-6">
                    No projects assigned
                  </p>
                ) : (
                  <div className="space-y-1 pl-6">
                    {detailProjects.map((p) => (
                      <p key={p.project_id} className="text-xs text-slate-600">
                        {p.project_name}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* Quick actions */}
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start h-8 text-xs"
                  onClick={() => {
                    setSelectedUser(detailUser);
                    setEditUserForm({
                      name: detailUser.name,
                      email: detailUser.email,
                    });
                    setShowEditUserDialog(true);
                  }}
                >
                  <Pencil className="h-3 w-3 mr-2" />
                  Edit Profile
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start h-8 text-xs"
                  onClick={() => {
                    setSelectedUser(detailUser);
                    setNewRole(detailUser.role);
                    setNewIsPlanner(!!detailUser.is_planner);
                    setShowRoleDialog(true);
                  }}
                  /* Self allowed: admin may self-grant Planner (spec Example B);
                     the dialog disables the tier select for self. */
                >
                  <Edit className="h-3 w-3 mr-2" />
                  Change Role
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start h-8 text-xs"
                  onClick={() => {
                    setSelectedUser(detailUser);
                    setTempPassword("");
                    setShowResetPasswordDialog(true);
                  }}
                  disabled={detailUser.id === user?.id}
                >
                  <KeyRound className="h-3 w-3 mr-2" />
                  Reset Password
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bulk Import Dialog */}
      {/* Edit User Profile Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
            <DialogDescription>
              Update name or email for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editUserForm.name}
                onChange={(e) =>
                  setEditUserForm((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editUserForm.email}
                onChange={(e) =>
                  setEditUserForm((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                placeholder="Email address"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowEditUserDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditUser}
              disabled={
                submitting ||
                (!editUserForm.name.trim() && !editUserForm.email.trim())
              }
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showBulkImportDialog}
        onOpenChange={setShowBulkImportDialog}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Import Users</DialogTitle>
            <DialogDescription>
              Import multiple users at once. Each line should contain: Name,
              Email, Role
            </DialogDescription>
          </DialogHeader>

          {bulkImportResult ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700 font-medium mb-1">
                  Import complete
                </p>
                <p className="text-xs text-green-600">
                  Created: {bulkImportResult.created} | Skipped:{" "}
                  {bulkImportResult.skipped} | Total: {bulkImportResult.total}
                </p>
              </div>
              {bulkImportResult.errors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <p className="text-xs font-medium text-amber-700 mb-1">
                    Issues:
                  </p>
                  {bulkImportResult.errors.map((err, i) => (
                    <p key={i} className="text-xs text-amber-600">
                      {err}
                    </p>
                  ))}
                </div>
              )}
              <Button
                className="w-full"
                onClick={() => {
                  setShowBulkImportDialog(false);
                  setBulkImportResult(null);
                  setBulkCsvText("");
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload CSV
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <p className="text-xs text-slate-400 self-center">
                  or paste below
                </p>
              </div>
              <textarea
                className="w-full h-40 border rounded-md p-3 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-slate-300"
                placeholder={
                  "John Doe, john@company.com, member\nJane Smith, jane@company.com, member"
                }
                value={bulkCsvText}
                onChange={(e) => setBulkCsvText(e.target.value)}
              />
              <p className="text-xs text-slate-400">
                Valid roles: member{canGrantAdmin() ? ", admin" : ""}. Per-project
                roles (view / collaborate / plan) are assigned in each project's
                Members panel.
              </p>
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setShowBulkImportDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkImport}
                  disabled={submitting || !bulkCsvText.trim()}
                >
                  {submitting && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Import Users
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Account / Invite User Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Account</DialogTitle>
            <DialogDescription>
              Create a new user account. A temporary password will be generated.
            </DialogDescription>
          </DialogHeader>

          {tempPassword ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700 mb-2 font-medium">
                  Account created successfully!
                </p>
                <p className="text-xs text-green-600 mb-3">
                  Share this temporary password with the user:
                </p>
                <div className="flex items-center gap-2 bg-white rounded border border-green-200 p-2">
                  <code className="flex-1 text-sm font-mono">
                    {tempPassword}
                  </code>
                  <Button size="sm" variant="ghost" onClick={copyToClipboard}>
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  The user will be prompted to change this on first login.
                </p>
              </div>
              <Button
                className="w-full"
                onClick={() => {
                  setShowInviteDialog(false);
                  setTempPassword("");
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleInviteUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-name">Name</Label>
                <Input
                  id="invite-name"
                  value={inviteForm.name}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, name: e.target.value })
                  }
                  placeholder="John Doe"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, email: e.target.value })
                  }
                  placeholder="john@company.com"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select
                  value={inviteForm.role}
                  onValueChange={(v) =>
                    setInviteForm({ ...inviteForm, role: v })
                  }
                >
                  <SelectTrigger id="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {canGrantAdmin() && (
                      <SelectItem value={ORG_ROLE.ADMIN}>Admin</SelectItem>
                    )}
                    <SelectItem value={ORG_ROLE.MEMBER}>Member</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">
                  This sets the organization role only. Project access (view /
                  collaborate / plan) is granted by each project's owning
                  planner.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="invite-planner"
                  data-testid="invite-planner-toggle"
                  checked={inviteForm.is_planner}
                  onCheckedChange={(v) =>
                    setInviteForm({ ...inviteForm, is_planner: !!v })
                  }
                />
                <Label
                  htmlFor="invite-planner"
                  className="text-sm font-normal cursor-pointer"
                >
                  Planner — can create and own projects
                </Label>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowInviteDialog(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Create Account
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={showResetPasswordDialog}
        onOpenChange={setShowResetPasswordDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Generate a new temporary password for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>

          {tempPassword ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700 mb-2 font-medium">
                  Password reset successfully!
                </p>
                <p className="text-xs text-green-600 mb-3">
                  New temporary password:
                </p>
                <div className="flex items-center gap-2 bg-white rounded border border-green-200 p-2">
                  <code className="flex-1 text-sm font-mono">
                    {tempPassword}
                  </code>
                  <Button size="sm" variant="ghost" onClick={copyToClipboard}>
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  The user will be prompted to change this on next login.
                </p>
              </div>
              <Button
                className="w-full"
                onClick={() => {
                  setShowResetPasswordDialog(false);
                  setTempPassword("");
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                This will generate a new temporary password for the user. They
                will need to change it on their next login.
              </p>
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setShowResetPasswordDialog(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleResetPassword} disabled={submitting}>
                  {submitting && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Reset Password
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update role for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select
              value={newRole}
              onValueChange={setNewRole}
              disabled={selectedUser?.id === user?.id}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {canGrantAdmin() && (
                  <SelectItem value={ORG_ROLE.ADMIN}>Admin</SelectItem>
                )}
                <SelectItem value={ORG_ROLE.MEMBER}>Member</SelectItem>
              </SelectContent>
            </Select>
            {selectedUser?.id === user?.id && (
              <p className="text-xs text-amber-600">
                You can change only your own Planner grant — your organization
                tier is locked to prevent locking yourself out (spec Example B).
              </p>
            )}
            <div className="flex items-center gap-2">
              <Checkbox
                id="role-planner"
                data-testid="role-planner-toggle"
                checked={newIsPlanner}
                onCheckedChange={(v) => setNewIsPlanner(!!v)}
              />
              <Label
                htmlFor="role-planner"
                className="text-sm font-normal cursor-pointer"
              >
                Planner — can create and own projects
              </Label>
            </div>
            <p className="text-xs text-slate-400">
              Organization role only. Project access (view / collaborate /
              plan) is granted by each project's owning planner.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRoleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRole} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Account Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Ban className="h-5 w-5" />
              Suspend Account
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to suspend {selectedUser?.name}? They will
              not be able to log in until reactivated.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowStatusDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleUpdateStatus("suspended")}
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Suspend Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Remove User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {selectedUser?.name}? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};  

export default AdminUsersPage;
